import { lazy } from 'react';
import { ICONS } from '../Utils/constants/icons';
import { isVisibleForChannel } from './routeVisibility';

export const FULLPAGE_ROUTES = [
  'ROLLINGREDEMPTIONREPORT',
  'REDEMPTIONREPORT',
  'APPOINTMENTSREPORT',
  'FUNNELREPORT',
  'WAVEACTIVITIESREPORT',
  'OUTBOUNDCALLSANALYSIS',
  'INBOUNDCALLSANALYSIS',
];

/**
 * Route key -> page component.
 *
 * Every page is `lazy()`, so its code is a separate chunk fetched on first
 * navigation rather than shipped in the entry bundle. `DynamicSection` supplies
 * the Suspense boundary — it is the only place that reads a value from here.
 *
 * The KEYS stay eagerly available and that is deliberate: `resolveRouteKey`
 * indexes them (KEY_BY_NORMALIZED below) to decide which server-provided routes
 * this build can render, which in turn drives the sidebar and URL resolution.
 * `lazy()` returns synchronously without fetching, so `Object.keys` is
 * unchanged and the visibility layer never has to wait on a chunk.
 *
 * This map is now unconditional. Three entries used to be gated on
 * `checkFeature('WELCOME_FEATURES' | 'MASSIVE_FEATURES' | 'PRACTICES_FEATURES')`,
 * which decided at bundle-eval time whether a page existed at all. That is the
 * server's answer now: `ITSRoutes` carries the module a route belongs to, and a
 * route belonging to a disabled module is never served, so the sidebar and URL
 * resolution never reach the key. Two mechanisms deciding the same thing is how
 * they come to disagree.
 *
 * The gates could not have survived in any case: the flags moved into the
 * bootstrap payload, and these maps are built when the module is evaluated —
 * long before it arrives.
 *
 * webpackChunkName is not cosmetic — `chunkFilename` is
 * `chunk-[name].[chunkhash].js`, so without it these emit as numeric ids.
 */
const ROUTE_LOADERS = {
  PRIORITIES: () =>
    import(/* webpackChunkName: "priorities" */ '../Pages/PrioritiesPage'),
  ROLLINGREDEMPTIONREPORT: () =>
    import(
      /* webpackChunkName: "rolling-redemption-report" */ '../Pages/Redemption/RollingRedemptionReport'
    ),
  REDEMPTIONREPORT: () =>
    import(
      /* webpackChunkName: "redemption-report" */ '../Pages/Redemption/RedemptionReport'
    ),
  APPOINTMENTSREPORT: () =>
    import(
      /* webpackChunkName: "appointments-report" */ '../Pages/AppointmentsReport'
    ),
  FUNNELREPORT: () =>
    import(/* webpackChunkName: "funnel-report" */ '../Pages/FunnelReport'),
  KANBAN: () => import(/* webpackChunkName: "kanban" */ '../Pages/Kanban'),
  WAVEACTIVITIESREPORT: () =>
    import(
      /* webpackChunkName: "wave-activities-report" */ '../Pages/WaveActivitiesReport'
    ),
  OUTBOUNDCALLSANALYSIS: () =>
    import(
      /* webpackChunkName: "pivot-report-outbound" */ '../Pages/PivotReport/Outbound'
    ),
  INBOUNDCALLSANALYSIS: () =>
    import(
      /* webpackChunkName: "pivot-report-inbound" */ '../Pages/PivotReport/Inbound'
    ),
  CUSTOMERS: () =>
    import(
      /* webpackChunkName: "all-customers" */ '../Pages/AllCustomers/Components'
    ),
  LEADS: () =>
    import(/* webpackChunkName: "leads" */ '../Pages/LeadsManagement/Leads'),
  LEAD_ORIGINS: () =>
    import(
      /* webpackChunkName: "lead-origins" */ '../Pages/LeadsManagement/LeadOrigin'
    ),
  BIRTHDAYS: () =>
    import(/* webpackChunkName: "birthdays" */ '../Pages/Birthdays/Components'),
  // One page, three routes: the segment is decided by the path, which reaches the
  // page as `sectionName` on the outlet context.
  LIGHT: () =>
    import(
      /* webpackChunkName: "customer-segment" */ '../Pages/CustomerSegments'
    ),
  CORPORATE: () =>
    import(
      /* webpackChunkName: "customer-segment" */ '../Pages/CustomerSegments'
    ),
  PREMIUMBANK: () =>
    import(
      /* webpackChunkName: "customer-segment" */ '../Pages/CustomerSegments'
    ),
  PURCHASEHISTORY: () =>
    import(
      /* webpackChunkName: "purchase-history" */ '../Pages/PurchaseHistory'
    ),
  SETTINGS: () =>
    import(/* webpackChunkName: "settings" */ '../Pages/SettingsPage'),
  SPOT: () =>
    import(
      /* webpackChunkName: "spot-campaigns" */ '../Pages/SpotCampaings/Components'
    ),
  WELCOME: () =>
    import(/* webpackChunkName: "welcome-calls" */ '../Pages/WelcomeCalls'),
  APPOINTMENT: () =>
    import(/* webpackChunkName: "appointments" */ '../Pages/AppointmentsPage'),
  //TODO: enable editor when ready
  EDITOR: () => import(/* webpackChunkName: "editor" */ '../Pages/Editor'),
  SENDS_ANALYSIS: () =>
    import(
      /* webpackChunkName: "sends-analysis" */ '../Pages/SendsAnalysis/Components'
    ),
  MASSIVE_SENDING: () =>
    import(
      /* webpackChunkName: "massive-messages" */ '../Pages/MassiveMessages'
    ),
  PRACTICES: () =>
    import(/* webpackChunkName: "practices" */ '../Pages/PracticesPage'),
  USER_MANAGEMENT: () =>
    import(/* webpackChunkName: "user-management" */ '../Pages/UserManagement'),
  ROLES_MANAGEMENT: () =>
    import(/* webpackChunkName: "role-management" */ '../Pages/RoleManagement'),
  BRANCHES_MANAGEMENT: () =>
    import(
      /* webpackChunkName: "branch-management" */ '../Pages/BranchManagement'
    ),
  WAVE_MANAGEMENT: () =>
    import(/* webpackChunkName: "wave-management" */ '../Pages/WaveManagement'),
  CUSTOMERS_CAMPAIGNS_MANAGEMENT: () =>
    import(
      /* webpackChunkName: "customers-campaigns-management" */ '../Pages/CustomersCampaignsManagement'
    ),
  ACTIVITYPROGRESS_MANAGEMENT: () =>
    import(
      /* webpackChunkName: "activity-progress-management" */ '../Pages/ActivityProgressManagement'
    ),
  PERMISSIONS_MANAGEMENT: () =>
    import(
      /* webpackChunkName: "permissions-management" */ '../Pages/PermissionsManagement'
    ),
  ROUTES_MANAGEMENT: () =>
    import(
      /* webpackChunkName: "route-management" */ '../Pages/RouteManagement'
    ),
};

/**
 * Route key -> lazy component, derived from the loaders above.
 *
 * Kept separate from ROUTE_LOADERS so the raw factory survives: React.lazy hides
 * the promise it creates, so without the loader there is no way to start a chunk
 * download before the route renders.
 */
export const ROUTES_COMPONENTS = Object.fromEntries(
  Object.entries(ROUTE_LOADERS).map(([key, load]) => [key, lazy(load)])
);

/**
 * Start fetching a route's chunk without navigating to it.
 *
 * Called on hover over a sidebar entry, which typically buys a few hundred
 * milliseconds before the click — usually the whole download. Safe to call
 * repeatedly: webpack caches the module promise, so every call after the first
 * resolves from memory, and the same promise is what React.lazy awaits when the
 * route finally renders.
 *
 * Silent on failure. A prefetch is speculative, so a chunk that will not load
 * should surface when the route is actually opened, not as an error nobody
 * asked for.
 */
export const preloadRoute = (routeKey) => {
  try {
    ROUTE_LOADERS[routeKey]?.()?.catch(() => {});
  } catch {
    /* nothing to do: the real navigation reports it */
  }
};

/**ROUTES constants**/
//all CRM's routes after login
export const APP_ROUTES = {
  PRIORITIES: '/priorities',
  ROLLINGREDEMPTIONREPORT: '/rollingRedemptionReport',
  REDEMPTIONREPORT: '/redemptionReport',
  APPOINTMENTSREPORT: '/appointmentsReport',
  FUNNELREPORT: '/funnelReport',
  KANBAN: '/kanban',
  OUTBOUNDCALLSANALYSIS: '/outboundPivotReport',
  INBOUNDCALLSANALYSIS: '/inboundPivotReport',
  CUSTOMERS: '/allContactsPage',
  LEADS: '/leadsPage',
  LEAD_ORIGINS: '/leadOriginPage',
  BIRTHDAYS: '/birthdaysPage',
  LIGHT: '/lightPage',
  CORPORATE: '/corporate',
  PREMIUMBANK: '/premiumBank',
  PURCHASEHISTORY: '/purchaseHistoryPage',
  SETTINGS: '/settingsPage',
  SPOT: '/spotCampaigns',
  WELCOME: '/welcomeCalls',
  APPOINTMENT: '/recalls-appointments',
  MASSIVE_SENDING: '/massiveSending',
  SENDS_ANALYSIS: '/sendsAnalysis',
  EDITOR: '/editor',
  PRACTICES: '/practices',
  USER_MANAGEMENT: '/userManagement',
  ROLES_MANAGEMENT: '/roleManagement',
  BRANCHES_MANAGEMENT: '/branchManagement',
  WAVE_MANAGEMENT: '/waveManagement',
  CUSTOMERS_CAMPAIGNS_MANAGEMENT: '/customersCampaignsManagement',
  ACTIVITYPROGRESS_MANAGEMENT: '/activityProgressManagement',
  WAVEACTIVITIESREPORT: '/waveActivitiesReport',
};

// Uppercase, drop any leading slash, and drop `_`/`-` so separator style does
// not matter: a target of 'userManagement' still matches USER_MANAGEMENT.
const normalize = (value) =>
  String(value ?? '')
    .replace(/^\/+/, '')
    .replace(/[_-]/g, '')
    .toUpperCase();

// Route keys indexed case-insensitively, so a server value in any casing can
// recover the exact key used by ROUTES_COMPONENTS / ROUTE_ICONS / i18n.
const KEY_BY_NORMALIZED = new Map(
  Object.keys(ROUTES_COMPONENTS).map((key) => [normalize(key), key])
);

// APP_ROUTES is routeKey -> '/path'. Inverted, it covers the routes whose
// target is a path that does not match its own key — 'recalls-appointments'
// for APPOINTMENT, 'lightPage' for LIGHT — which uppercasing alone cannot map.
const KEY_BY_APP_ROUTE_PATH = new Map(
  Object.entries(APP_ROUTES).map(([key, path]) => [normalize(path), key])
);

/**
 * Canonical route key for a server-provided route, or null if this build has no
 * page for it.
 *
 * Identity must never depend on `name` alone: the tree is server-driven and
 * `name` is not guaranteed to be a route key (it can be a GUID). Resolution
 * order, all case-insensitive:
 *   1. `name` matches a route key
 *   2. `target` matches a route key  (e.g. 'appointmentsReport' -> APPOINTMENTSREPORT)
 *   3. `target` matches an APP_ROUTES path (e.g. 'recalls-appointments' -> APPOINTMENT)
 */
export const resolveRouteKey = (route) => {
  const byName = KEY_BY_NORMALIZED.get(normalize(route?.name));
  if (byName) return byName;

  const target = normalize(route?.target);
  if (!target) return null;

  const byTargetKey = KEY_BY_NORMALIZED.get(target);
  if (byTargetKey) return byTargetKey;

  const byTargetPath = KEY_BY_APP_ROUTE_PATH.get(target);
  if (byTargetPath && Object.hasOwn(ROUTES_COMPONENTS, byTargetPath)) {
    return byTargetPath;
  }

  return null;
};

// The tree is server-driven, so it can name routes this build knows nothing
// about — a new DB row, or one whose page is behind an off feature flag.
// Dropping those beats rendering `<undefined />` into an error boundary.
const isRenderable = (route) =>
  Boolean(route?.target && resolveRouteKey(route));

const hasChildren = (route) => Boolean(route?.subroutes?.length);

// The tree as the sidebar should show it for the current channel: nesting
// preserved, leaves failing the channel or renderability check dropped, parents
// left with no children pruned.
export const renderRoutesTree = (routesTree, isLongChannel = false) =>
  (routesTree ?? []).reduce((acc, route) => {
    if (!isVisibleForChannel(route, isLongChannel)) {
      return acc;
    }

    if (hasChildren(route)) {
      const subroutes = renderRoutesTree(route.subroutes, isLongChannel);
      return subroutes.length ? [...acc, { ...route, subroutes }] : acc;
    }

    return isRenderable(route) ? [...acc, route] : acc;
  }, []);

// Every renderable leaf regardless of channel, depth-first — for route
// registration and for resolving the current location's name.
export const flattenRoutes = (routesTree) =>
  (routesTree ?? []).flatMap((route) => {
    if (hasChildren(route)) {
      return flattenRoutes(route.subroutes);
    }
    return isRenderable(route) ? [route] : [];
  });

//to prevent dependency cycle
export const ROUTE_ICONS = { ...ICONS };
/****/
